import { isValid as isSsnValid } from 'ssn-validator';
import * as ramda from 'ramda';

import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { NpiValidator } from '$shared/helpers/validators/NpiValidator';
import { DOBValidator } from '$shared/helpers/validators/DOBValidator';
import { DateValidator } from '$shared/helpers/validators/DateValidator';
import { EmailValidator } from '$shared/helpers/validators/EmailValidator';
import { CurrencyValidator } from '../validators/CurrencyValidator';

const notNull = (value: string) => {
  return !ramda.isNil(value);
};

const isNotEmpty = (value: string) => {
  return ramda.is(Number, value) || !ramda.isEmpty(value);
};

export const emailValidator = (isRequired = true): Validator => {
  return (value: string): ValidatorReturnType => {
    if (!isRequired && !value) {
      return true;
    }
    try {
      EmailValidator.validate(value);
      return true;
    } catch (err: any) {
      return err.message;
    }
  };
};

export const requiredValidator = (customMessage = '') => {
  return (value: string): ValidatorReturnType => {
    const valueTrimmed = value.trim();
    return (
      (notNull(valueTrimmed) && isNotEmpty(valueTrimmed)) ||
      customMessage ||
      UIErrorMessage.FieldRequired
    );
  };
};

export const passwordValidator = () => {
  return (value: string): ValidatorReturnType => {
    return (notNull(value) && isNotEmpty(value)) || UIErrorMessage.InvalidPassword;
  };
};

export const dateOfBirthValidator = () => {
  return (value: string): ValidatorReturnType => {
    if (!value) return true;

    try {
      DOBValidator.validate(value);
      return true;
    } catch (err: any) {
      return err.message;
    }
  };
};

export const numericValidator = (isRequired = true, shouldBePositive = false) => {
  return (value: string): ValidatorReturnType => {
    if (!isRequired && !value) {
      return true;
    }
    if (isNaN(value)) {
      return UIErrorMessage.InvalidNumber;
    }

    if (shouldBePositive) {
      return parseInt(value) > -1 || UIErrorMessage.NumberMustBePositive;
    }

    return true;
  };
};

export const ssnValidator = (isRequired = true) => {
  return (value: string): ValidatorReturnType => {
    if (!isRequired && !value) {
      return true;
    }
    return isSsnValid(value) || UIErrorMessage.InvalidSsn;
  };
};

export const npiValidator = (isRequired = true) => {
  return (npiNumber: string): ValidatorReturnType => {
    if (!isRequired && !npiNumber) {
      return true;
    }
    if (!npiNumber) {
      return false;
    }
    try {
      NpiValidator.validate(npiNumber);
      return true;
    } catch (err: any) {
      return err.message;
    }
  };
};

export const currencyNumberValidator = (isRequired = true) => {
  return (value: string): ValidatorReturnType => {
    if (!isRequired && !value) return true;
    if (!value) return false;
    try {
      CurrencyValidator.validate(value);
      return true;
    } catch (err: any) {
      return err.message;
    }
  };
};

export const taxIdValidator = (isRequired = true) => {
  return (value: string): ValidatorReturnType => {
    if (!isRequired && !value) {
      return true;
    }
    if (!value) {
      return false;
    }
    if (value.length !== 9) {
      return UIErrorMessage.InvalidTaxID;
    }
    return !ramda.is(Number, value) || UIErrorMessage.InvalidTaxID;
  };
};

export const pastDateValidator = (): ((value: string) => ValidatorReturnType) => {
  return (value: string): ValidatorReturnType => {
    if (!value) return false;
    try {
      DateValidator.pastDateValidator(value);
      return true;
    } catch (err: any) {
      return err.message;
    }
  };
};
